import React, { createContext, useContext, ReactNode } from 'react';
import { Department, DepartmentFormData } from '../types/department.types';
import { useDepartments } from '../hooks/useDepartments';

interface DepartmentsContextType {
  departments: Department[];
  selectedDepartment: Department | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: Error | null;
  fetchDepartments: () => Promise<Department[]>;
  fetchDepartmentById: (id: string) => Promise<Department>;
  createDepartment: (data: DepartmentFormData) => Promise<Department>;
  updateDepartment: (id: string, data: Partial<DepartmentFormData>) => Promise<Department>;
  deleteDepartment: (id: string) => Promise<void>;
  toggleDepartmentStatus: (id: string, isActive: boolean) => Promise<Department>;
  setSelectedDepartment: (department: Department | null) => void;
}

const DepartmentsContext = createContext<DepartmentsContextType | undefined>(undefined);

interface DepartmentsProviderProps {
  children: ReactNode;
}

export const DepartmentsProvider: React.FC<DepartmentsProviderProps> = ({ children }) => {
  const { 
    departments, 
    selectedDepartment, 
    isLoading, 
    isSubmitting, 
    error,
    fetchDepartments,
    fetchDepartmentById,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    toggleDepartmentStatus,
    setSelectedDepartment 
  } = useDepartments();

  return (
    <DepartmentsContext.Provider
      value={{
        departments,
        selectedDepartment,
        isLoading,
        isSubmitting,
        error,
        fetchDepartments,
        fetchDepartmentById,
        createDepartment,
        updateDepartment,
        deleteDepartment,
        toggleDepartmentStatus,
        setSelectedDepartment,
      }}
    >
      {children}
    </DepartmentsContext.Provider>
  );
};

export const useDepartmentsContext = () => {
  const context = useContext(DepartmentsContext);
  if (context === undefined) {
    throw new Error('useDepartmentsContext must be used within a DepartmentsProvider');
  }
  return context;
};
